import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $isLinkNode, type LinkNode } from '@lexical/link';
import { ExternalLink, Pencil, Unlink, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FloatingLinkEditorProps {
  onEdit: (url: string) => void;
  onUnlink: () => void;
  /**
   * Open a URL externally. Editor-core ships a `window.open` default so the
   * editor remains platform-agnostic; the Tauri client injects
   * `@tauri-apps/plugin-opener`'s `openUrl`, the Mini App will inject
   * `Telegram.WebApp.openLink`.
   */
  onOpenLink?: (url: string) => void;
  /**
   * Visual density. `comfortable` enlarges touch targets for mobile use
   * (Mini App). `compact` is the default and matches the desktop client.
   */
  size?: 'compact' | 'comfortable';
  /**
   * Suppress the floating bubble. Set this to `true` when the consumer is
   * showing the link edit popover so the two UIs don't overlap.
   */
  hidden?: boolean;
}

const defaultOpenLink = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function FloatingLinkEditor({
  onEdit,
  onUnlink,
  onOpenLink = defaultOpenLink,
  size = 'compact',
  hidden = false,
}: FloatingLinkEditorProps) {
  const { t } = useTranslation('editor');
  const [editor] = useLexicalComposerContext();
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDomRect, setLinkDomRect] = useState<DOMRect | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setLinkUrl('');
          setLinkDomRect(null);
          return;
        }

        const anchorNode = selection.anchor.getNode();
        let linkNode: LinkNode | null = null;
        let current = anchorNode.getParent();
        while (current !== null) {
          if ($isLinkNode(current)) {
            linkNode = current;
            break;
          }
          current = current.getParent();
        }

        if (!linkNode) {
          setLinkUrl('');
          setLinkDomRect(null);
          return;
        }

        setLinkUrl(linkNode.getURL());

        // Get DOM rect from the link element
        const linkDomElement = editor.getElementByKey(linkNode.getKey());
        if (linkDomElement) {
          setLinkDomRect(linkDomElement.getBoundingClientRect());
        }
      });
    });
  }, [editor]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(linkUrl);
  }, [linkUrl]);

  const handleOpen = useCallback(() => {
    onOpenLink(linkUrl);
  }, [linkUrl, onOpenLink]);

  if (hidden || !linkUrl || !linkDomRect) return null;

  const truncated = linkUrl.length > 40 ? `${linkUrl.slice(0, 37)}...` : linkUrl;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: linkDomRect.bottom + 6,
    left: linkDomRect.left,
    zIndex: 50,
  };

  const isComfortable = size === 'comfortable';
  const containerCls = isComfortable
    ? 'flex items-center gap-1 rounded-lg border bg-popover px-2 py-1.5 shadow-md'
    : 'flex items-center gap-1 rounded-md border bg-popover px-2 py-1 shadow-md';
  const labelCls = isComfortable
    ? 'max-w-56 truncate px-1 text-sm text-blue-500 underline hover:text-blue-600'
    : 'max-w-48 truncate text-xs text-blue-500 underline hover:text-blue-600';
  const iconBtnCls = isComfortable
    ? 'inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent'
    : 'rounded p-1 hover:bg-accent';
  const dangerBtnCls = isComfortable
    ? 'inline-flex h-9 w-9 items-center justify-center rounded-md text-destructive hover:bg-destructive/10'
    : 'rounded p-1 text-destructive hover:bg-destructive/10';
  const iconSize = isComfortable ? 'h-4 w-4' : 'h-3 w-3';

  return createPortal(
    <div ref={editorRef} style={style} className={containerCls}>
      <button
        type="button"
        onClick={handleOpen}
        className={labelCls}
        title={linkUrl}
      >
        {truncated}
      </button>
      <button
        type="button"
        onClick={() => { onEdit(linkUrl); }}
        className={iconBtnCls}
        title={t('linkEdit')}
        aria-label={t('linkEdit')}
      >
        <Pencil className={iconSize} />
      </button>
      <button
        type="button"
        onClick={handleCopy}
        className={iconBtnCls}
        title={t('linkCopy')}
        aria-label={t('linkCopy')}
      >
        <Copy className={iconSize} />
      </button>
      <button
        type="button"
        onClick={handleOpen}
        className={iconBtnCls}
        title={t('linkOpen')}
        aria-label={t('linkOpen')}
      >
        <ExternalLink className={iconSize} />
      </button>
      <button
        type="button"
        onClick={onUnlink}
        className={dangerBtnCls}
        title={t('linkRemove')}
        aria-label={t('linkRemove')}
      >
        <Unlink className={iconSize} />
      </button>
    </div>,
    document.body,
  );
}
